import React from 'react';
import { makeStyles } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import AccountTreeOutlinedIcon from '@material-ui/icons/AccountTreeOutlined';
import { FORM_COLORS } from '../constants';
import { TabComponentProps, PublicationVariant } from '../types';
import { InfoBox } from '../components/InfoBox';

const useStyles = makeStyles(() => ({
  emptyState: {
    border: `1px dashed ${FORM_COLORS.border}`,
    borderRadius: 8,
    padding: '32px 20px',
    textAlign: 'center',
    marginBottom: 14,
  },
  emptyIcon: {
    fontSize: 28,
    color: FORM_COLORS.textSecondary,
    marginBottom: 10,
  },
  emptyTitle: {
    fontSize: 13,
    fontWeight: 500,
    color: FORM_COLORS.textSecondary,
    margin: '0 0 4px',
  },
  emptyText: {
    fontSize: 12,
    color: FORM_COLORS.textSecondary,
    margin: '0 0 14px',
  },
  outlineButton: {
    background: 'transparent',
    color: FORM_COLORS.pink,
    border: `1.5px solid ${FORM_COLORS.pink}`,
    borderRadius: 8,
    padding: '6px 14px',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontFamily: 'inherit',
    transition: 'background .15s',
    '&:hover': {
      background: FORM_COLORS.pinkLight,
    },
  },
  tableWrap: {
    border: `0.5px solid ${FORM_COLORS.border}`,
    borderRadius: 8,
    overflow: 'hidden',
    marginBottom: 14,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
  },
  th: {
    background: FORM_COLORS.bg,
    padding: '9px 12px',
    textAlign: 'left',
    fontWeight: 500,
    color: FORM_COLORS.textSecondary,
    fontSize: 12,
    borderBottom: `0.5px solid ${FORM_COLORS.border}`,
  },
  td: {
    padding: '8px 10px',
    borderBottom: `0.5px solid ${FORM_COLORS.border}`,
    verticalAlign: 'middle',
  },
  cellInput: {
    width: '100%',
    padding: '5px 8px',
    fontSize: 12,
    border: `0.5px solid ${FORM_COLORS.border}`,
    borderRadius: 6,
    fontFamily: 'inherit',
    background: FORM_COLORS.white,
    color: FORM_COLORS.text,
    outline: 'none',
    boxSizing: 'border-box',
    '&:focus': {
      borderColor: FORM_COLORS.pink,
    },
  },
  deleteButton: {
    background: 'none',
    border: `0.5px solid ${FORM_COLORS.redBorder}`,
    borderRadius: 8,
    padding: '4px 8px',
    color: FORM_COLORS.red,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
  },
}));

const createVariant = (): PublicationVariant => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  sku: '',
  attribute: '',
  value: '',
  stock: '',
  differentialPrice: '',
});

const COLUMNS: { key: keyof Omit<PublicationVariant, 'id'>; label: string; type?: string }[] = [
  { key: 'sku', label: 'SKU variante' },
  { key: 'attribute', label: 'Atributo' },
  { key: 'value', label: 'Valor' },
  { key: 'stock', label: 'Stock', type: 'number' },
  { key: 'differentialPrice', label: 'Precio diferencial' },
];

export const VariantsTab = ({ values, onChange }: TabComponentProps) => {
  const classes = useStyles();
  const { variants } = values;

  const addVariant = () => onChange('variants', [...variants, createVariant()]);

  const updateVariant = (
    id: string,
    field: keyof Omit<PublicationVariant, 'id'>,
    value: string,
  ) => {
    onChange(
      'variants',
      variants.map((variant) =>
        variant.id === id ? { ...variant, [field]: value } : variant,
      ),
    );
  };

  const removeVariant = (id: string) => {
    onChange(
      'variants',
      variants.filter((variant) => variant.id !== id),
    );
  };

  return (
    <div>
      <InfoBox tone="gray">
        Las variantes representan versiones del mismo producto (color, capacidad, tamaño) bajo una
        única publicación. Son opcionales.
      </InfoBox>

      {variants.length === 0 ? (
        <div className={classes.emptyState}>
          <AccountTreeOutlinedIcon className={classes.emptyIcon} />
          <p className={classes.emptyTitle}>Sin variantes configuradas</p>
          <p className={classes.emptyText}>
            Podés agregar variantes si el producto tiene distintas versiones (color, tamaño, etc.).
          </p>
          <button type="button" className={classes.outlineButton} onClick={addVariant}>
            <AddIcon style={{ fontSize: 16 }} />
            <span>Agregar primera variante</span>
          </button>
        </div>
      ) : (
        <>
          <div className={classes.tableWrap}>
            <table className={classes.table}>
              <thead>
                <tr>
                  {COLUMNS.map((column) => (
                    <th key={column.key} className={classes.th}>
                      {column.label}
                    </th>
                  ))}
                  <th className={classes.th} style={{ width: 60 }}>
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {variants.map((variant) => (
                  <tr key={variant.id}>
                    {COLUMNS.map((column) => (
                      <td key={column.key} className={classes.td}>
                        <input
                          className={classes.cellInput}
                          type={column.type || 'text'}
                          aria-label={column.label}
                          value={variant[column.key]}
                          onChange={(event) =>
                            updateVariant(variant.id, column.key, event.target.value)
                          }
                        />
                      </td>
                    ))}
                    <td className={classes.td}>
                      <button
                        type="button"
                        aria-label="Eliminar variante"
                        className={classes.deleteButton}
                        onClick={() => removeVariant(variant.id)}
                      >
                        <DeleteOutlineIcon style={{ fontSize: 16 }} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" className={classes.outlineButton} onClick={addVariant}>
            <AddIcon style={{ fontSize: 16 }} />
            <span>Agregar variante</span>
          </button>
        </>
      )}
    </div>
  );
};
